import { keyframes, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { rem } from "polished";
import React from "react";

import { Flex, Label, Spacer } from "../base";

const rippleAnimation = (size: number) => keyframes`
  0% {
    top: ${rem(size / 2)};
    left: ${rem(size / 2)};
    width: 0;
    height: 0;
    opacity: 1;
  }

  100% {
    top: 0px;
    left: 0px;
    width: ${rem(size)};
    height: ${rem(size)};
    opacity: 0;
  }
`;

const Ripple = styled.div<{ size: number }>`
  display: inline-block;
  position: relative;
  width: ${({ size }) => rem(size + 4)};
  height: ${({ size }) => rem(size + 4)};

  & div {
    position: absolute;
    border: 4px solid ${({ theme }) => theme.color.highlight};
    opacity: 1;
    border-radius: 50%;
    animation: ${({ size }) => rippleAnimation(size)} 1.5s
      cubic-bezier(0, 0.2, 0.8, 1) infinite;
  }

  & div:nth-child(2) {
    animation-delay: -0.5s;
  }
`;

Ripple.defaultProps = {
  size: 72,
};

export const LoadingRipple = ({ size }) => (
  <Ripple size={size}>
    <div />
    <div />
  </Ripple>
);

const variants = {
  rotateX: { rotateX: 360, rotateY: 360 },
  rotateY: { rotateY: 360, rotateZ: 360, scale: 1.07 },
  rotateZ: { rotateZ: 360, rotateX: 360, scale: 0.92 },
  done: {
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    scale: 1,
    transition: {
      ease: "linear",
      duration: 1,
      flip: 0,
    },
  },
};

// const useVariants = {
//   rotateX: { fill: 'transparent' },
//   rotateY: { fill: 'transparent' },
//   rotateZ: { fill: 'transparent' },
//   done: { fill: 'url(#linear)' },
// }

const SpinningWheel = ({
  animateVariant = "rotateX",
  duration = 1,
  ...props
}) => {
  const { color } = useTheme();
  const logoStops = color.logoStops?.filter(
    (stop) => stop.toLowerCase() !== "#ffffff"
  );
  return (
    <motion.svg
      animate={animateVariant}
      initial={{ rotateX: 0, rotateY: 0, rotateZ: 0 }}
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        right: 0,
        originX: "center",
        originY: "center",
        originZ: "center",
        zIndex: 1,
      }}
      transition={{
        ease: "linear",
        duration: duration * 2.9,
        // delay: duration - 2 * 0.02,
        flip: Infinity,
      }}
      variants={variants}
      viewBox="0 0 50 50"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      {...props}
    >
      <defs>
        <path
          d="M25 0c13.807 0 25 11.193 25 25S38.807 50 25 50 0 38.807 0 25 11.193 0 25 0z"
          id="a"
        />
        <clipPath id="b">
          <use xlinkHref="#a" />
        </clipPath>
        <linearGradient id="linear" x1="0%" x2="100%" y1="0%" y2="100%">
          {logoStops?.map((stop, idx) => {
            const colorStops = [
              ...logoStops.slice(idx),
              ...logoStops.slice(0, idx),
            ];

            return (
              <motion.stop
                key={`stop_${idx}`}
                animate={{
                  stopColor: colorStops,
                }}
                offset={`${((idx / color.logoStops.length) * 100).toFixed(0)}%`}
                stopColor={stop}
                transition={{
                  yoyo: Infinity,
                  ease: "linear",
                  duration: 6 * duration,
                }}
              />
            );
          })}
        </linearGradient>
      </defs>
      <motion.use
        clipPath="url(#b)"
        fill="transparent"
        // animate={animateVariant}
        // variants={useVariants}
        stroke="url(#linear)"
        strokeWidth="6"
        xlinkHref="#a"
      />
    </motion.svg>
  );
};

const containerVariants = {
  unchecked: {
    opacity: 0,
  },
  checked: {
    opacity: 1,
  },
};

const checkVariants = {
  unchecked: {
    pathLength: 0,
  },
  checked: {
    pathLength: 1,

    transition: {
      duration: 0.8,
    },
  },
};

const AnimatedCheck = () => {
  const theme = useTheme();
  return (
    <motion.svg
      animate="checked"
      height="440"
      initial="unchecked"
      style={{
        position: "absolute",
        width: "66%",
        height: "66%",
        left: "14%",
        top: "25%",

        originX: "center",
        originY: "center",
        originZ: "center",

        zIndex: 0,
      }}
      variants={containerVariants}
      viewBox="0 0 440 440"
      width="440"
    >
      <motion.path
        d="M 0 128.666 L 128.658 257.373 L 341.808 0"
        fill="transparent"
        stroke={theme.color.highlight}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="80"
        transform="translate(54.917 68.947) rotate(-4 170.904 128.687)"
        variants={checkVariants}
      />
    </motion.svg>
  );
};

const GradientFun = ({ size = 100, done = false, ...props }) => (
  <motion.div
    style={{
      display: "flex",
      justifyContent: "center",
      position: "relative",

      width: rem(size),
      height: rem(size),
    }}
    {...props}
  >
    <SpinningWheel animateVariant={done ? "done" : "rotateX"} duration={1.1} />
    <SpinningWheel animateVariant={done ? "done" : "rotateY"} duration={1.2} />
    <SpinningWheel animateVariant={done ? "done" : "rotateZ"} />

    {done ? <AnimatedCheck /> : null}
  </motion.div>
);

export default GradientFun;

export const FlexLoadingBlock = ({ text = "Loading...", ...props }) => (
  <Flex center column>
    <GradientFun {...props} />
    {text ? (
      <>
        <Spacer size={20} /> <Label>{text}</Label>
      </>
    ) : null}
  </Flex>
);

export const LoadingOrb = (props) => <GradientFun {...props} />;
